import { Controller } from 'stimulus';

const OPTION_TYPES = ['select', 'radio', 'checkbox'];

// Builder controller, attached to each section row.
// Manages the section's "Show when" UI: parent-field dropdown (across all
// other sections), condition checkboxes, and hidden inputs round-tripped
// to the server.
export default class extends Controller {
  static targets = ['parentSelect', 'parentFieldIdInput', 'parentFieldKeyInput',
                    'conditionContainer', 'conditionCheckboxes', 'conditionHidden'];

  connect() {
    this.refreshTimeout = null;
    this.cocoonHandler = () => this.scheduleRefresh();
    this.fieldChangedHandler = () => this.scheduleRefresh();

    $(document).on('cocoon:after-insert.pf-section-condition cocoon:after-remove.pf-section-condition', this.cocoonHandler);
    document.addEventListener('public-form-field-changed', this.fieldChangedHandler);

    setTimeout(() => this.rebuildParentDropdown(), 50);
  }

  disconnect() {
    $(document).off('.pf-section-condition');
    document.removeEventListener('public-form-field-changed', this.fieldChangedHandler);
  }

  scheduleRefresh() {
    clearTimeout(this.refreshTimeout);
    this.refreshTimeout = setTimeout(() => this.rebuildParentDropdown(), 50);
  }

  parentChanged() {
    const locator = this.parentSelectTarget.value;

    if (!locator) {
      this.parentFieldIdInputTarget.value = '';
      this.parentFieldKeyInputTarget.value = '';
      this.conditionContainerTarget.classList.add('hidden');
      this.conditionCheckboxesTarget.innerHTML = '';
      return;
    }

    const fieldRow = this.findFieldRowByLocator(locator);
    if (!fieldRow) return;

    this.parentFieldIdInputTarget.value = this.getFieldId(fieldRow) || '';
    this.parentFieldKeyInputTarget.value = locator;

    const options = this.getOptionsFromFieldRow(fieldRow);
    if (options.length === 0) {
      this.conditionContainerTarget.classList.add('hidden');
      this.conditionCheckboxesTarget.innerHTML = '';
      return;
    }

    this.conditionContainerTarget.classList.remove('hidden');
    this.renderCheckboxes(options);
  }

  rebuildParentDropdown() {
    if (!this.hasParentSelectTarget) return;

    const savedParentFieldId = this.element.dataset.publicFormSectionConditionSavedParentFieldId;
    const savedConditionValues = JSON.parse(this.element.dataset.publicFormSectionConditionSavedConditionValues || '[]');
    const currentSelection = this.parentSelectTarget.value;

    this.parentSelectTarget.innerHTML = '<option value="">Always show (no condition)</option>';

    let restoredLocator = null;

    this.eachExternalFieldRow((fieldRow, sectionRow) => {
      const type = this.getFieldType(fieldRow);
      if (!OPTION_TYPES.includes(type)) return;

      const sk = this.getSectionKey(sectionRow);
      const fk = this.getFieldKey(fieldRow);
      if (!sk || !fk) return;

      const locator = `${sk}:${fk}`;
      const label = this.getFieldLabel(fieldRow) || 'Untitled';

      const opt = document.createElement('option');
      opt.value = locator;
      opt.textContent = label;
      this.parentSelectTarget.appendChild(opt);

      const fieldDbId = this.getFieldId(fieldRow);
      if (savedParentFieldId && fieldDbId && fieldDbId.toString() === savedParentFieldId.toString()) {
        restoredLocator = locator;
      }
    });

    if (currentSelection && this.parentSelectTarget.querySelector(`option[value="${this.cssEscape(currentSelection)}"]`)) {
      this.parentSelectTarget.value = currentSelection;
    } else if (restoredLocator) {
      this.parentSelectTarget.value = restoredLocator;
    }

    const selectedLocator = this.parentSelectTarget.value;
    if (selectedLocator) {
      const fieldRow = this.findFieldRowByLocator(selectedLocator);
      if (fieldRow) {
        this.parentFieldIdInputTarget.value = this.getFieldId(fieldRow) || '';
        this.parentFieldKeyInputTarget.value = selectedLocator;

        const options = this.getOptionsFromFieldRow(fieldRow);
        if (options.length > 0) {
          this.conditionContainerTarget.classList.remove('hidden');
          this.renderCheckboxes(options, savedConditionValues.length ? savedConditionValues : null);
          // Consume the saved values so subsequent rebuilds don't re-restore
          this.element.dataset.publicFormSectionConditionSavedConditionValues = '[]';
          return;
        }
      }
    }

    this.parentFieldIdInputTarget.value = '';
    this.parentFieldKeyInputTarget.value = '';
    this.conditionContainerTarget.classList.add('hidden');
    this.conditionCheckboxesTarget.innerHTML = '';
  }

  renderCheckboxes(options, checkedValues) {
    const inputName = this.conditionHiddenTarget.name.replace('[]', '');

    if (!checkedValues) {
      checkedValues = [];
      this.conditionCheckboxesTarget.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        checkedValues.push(cb.value);
      });
    }

    this.conditionCheckboxesTarget.innerHTML = options.map(option => {
      const checked = checkedValues.includes(option) ? 'checked' : '';
      return `
        <div class="flex items-center">
          <input type="checkbox"
            name="${inputName}[]"
            value="${this.escapeHtml(option)}"
            ${checked}
            class="form-checkbox h-4 w-4 text-blue-600">
          <label class="ml-2 text-sm text-gray-700">${this.escapeHtml(option)}</label>
        </div>
      `;
    }).join('');
  }

  // ---- Helpers ----

  eachExternalFieldRow(callback) {
    const formScope = this.element.closest('form') || document;
    const sectionRows = formScope.querySelectorAll('[data-controller~="public-form-section-condition"]');
    sectionRows.forEach((sectionRow) => {
      if (sectionRow === this.element) return;
      sectionRow.querySelectorAll('[data-controller~="public-form-section-condition-field"]').forEach((fieldRow) => {
        callback(fieldRow, sectionRow);
      });
    });
  }

  findFieldRowByLocator(locator) {
    let result = null;
    this.eachExternalFieldRow((fieldRow, sectionRow) => {
      if (result) return;
      if (`${this.getSectionKey(sectionRow)}:${this.getFieldKey(fieldRow)}` === locator) {
        result = fieldRow;
      }
    });
    return result;
  }

  getSectionKey(sectionRow) {
    const input = sectionRow.querySelector('input[name*="public_form_sections_attributes"]');
    if (!input) return null;
    const m = input.name.match(/public_form_sections_attributes\]\[([^\]]+)\]/);
    return m ? m[1] : null;
  }

  getFieldKey(fieldRow) {
    const input = fieldRow.querySelector(
      'input[name*="public_form_fields_attributes"], select[name*="public_form_fields_attributes"], textarea[name*="public_form_fields_attributes"]'
    );
    if (!input) return null;
    const m = input.name.match(/public_form_fields_attributes\]\[([^\]]+)\]/);
    return m ? m[1] : null;
  }

  getFieldId(fieldRow) {
    return fieldRow.dataset.fieldRowDbId || null;
  }

  getFieldLabel(fieldRow) {
    const input = fieldRow.querySelector('input[name$="[label]"]');
    return input ? input.value : '';
  }

  getFieldType(fieldRow) {
    const select = fieldRow.querySelector('select[name$="[field_type]"]');
    return select ? select.value : '';
  }

  getOptionsFromFieldRow(fieldRow) {
    const textarea = fieldRow.querySelector('textarea[name$="[options]"]');
    if (!textarea) return [];
    return (textarea.value || '').split('\n').map(s => s.trim()).filter(s => s.length > 0);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  cssEscape(s) {
    return s.replace(/"/g, '\\"');
  }
}
